import React from "react";
import withShipment from "../../withShipment";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import Autocomplete from "@material-ui/lab/Autocomplete";

import TopToolbar from "../global/subcomponents/topToolbar/TopToolbar";
import FeedbackBar from "../feedbackBar/FeedbackBar";
import SearchableTable from "../global/Search/SearchableTable";

import { editCategory } from "../../redux/actions/settings";
import { userInfoSelector } from "../../redux/selectors/auth";
import { categorySelector } from "../../redux/selectors/settings";
import { clearStatus } from "../../redux/actions/global";
import { BACK } from "../reading/masterReading/constants";
import { expenses, TRANSACTIONS_PAGES } from "./constants";

import "./Transaction.css";

class TransactionCategory extends SearchableTable {
    constructor(props) {
        super(props);
        this.state = {
            categories: this.props.categories || [],
            feedbackBarOpen: false,
            feedbackStatus: "",
            feedbackMessage: "",
            showDeleteDialog: false,
            rowToDelete: null,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.categories !== this.props.categories) {
            if (this.props.categories && this.props.categories.length > 0) {
                this.setState({ categories: [...this.props.categories] });
            }
        }
    }

    getContextualMenuItems = () => [
        {
            icon: <KeyboardBackspaceIcon />,
            onClick: () => {
                this.props.setCurrentPage(TRANSACTIONS_PAGES.TABLE);
                this.props.setBackMenu(false);
            },
            title: BACK,
        },
        {
            title: "Save",
            icon: <SaveIcon />,
            onClick: this.handleSave,
            disabled: this.isSaveDisabled(),
        },
    ];

    handleSave = () => {
        const categoryNames = this.state.categories.map((m) =>
            (m.name || "").trim().toLowerCase()
        );
        const hasDuplicates = new Set(categoryNames).size !== categoryNames.length;

        if (hasDuplicates) {
            this.openFeedbackBar("error", "Duplicate category not allowed.");
            return;
        }

        const callback = (error, response) => {
            if (error) {
                this.openFeedbackBar("error", error);
            } else {
                this.openFeedbackBar("success", response);
            }
        };

        // ✅ Send both name + expenseType
        this.props.editCategory(this.props.userInfo.selectedLocationId,this.state.categories, callback);
    };

    isSaveDisabled = () => {
        return this.state.categories.some(
            (category) => !category.name
        );
    };

    addCategoryRow = () => {
        this.setState((prevState) => ({
            categories: [
                ...prevState.categories,
                { name: "", expenseType: "", isNew: true },
            ],
        }));
    };

    removeCategoryRow = (index) => {
        this.setState((prevState) => ({
            categories: prevState.categories.filter((_, i) => i !== index),
        }));
    };

    handleCategoryChange = (e, index) => {
        const { name, value } = e.target;
        this.setState((prevState) => {
            const updatedCategory = [...prevState.categories];
            updatedCategory[index] = { ...updatedCategory[index], [name]: value };
            return { categories: updatedCategory };
        });
    };

    handleExpenseTypeChange = (value, index) => {
        this.setState((prevState) => {
            const updatedCategory = [...prevState.categories];
            updatedCategory[index] = { ...updatedCategory[index], expenseType: value };
            return { categories: updatedCategory };
        });
    };

    openFeedbackBar = (status, message) => {
        this.setState({
            feedbackStatus: status,
            feedbackMessage: message,
            feedbackBarOpen: true,
        });
    };

    closeFeedbackBar = () => {
        this.setState(
            { feedbackBarOpen: false, feedbackStatus: "", feedbackMessage: "" },
            () => this.props.clearStatus()
        );
    };

render() {
  const { categories } = this.state;
  const hasCompanyIds = this.props.userInfo?.isOwner;

  return (
    <>
      <TopToolbar pageName="Add Description" menuItems={this.getContextualMenuItems()} />

      <FeedbackBar
        open={this.state.feedbackBarOpen}
        handleClose={this.closeFeedbackBar}
        severity={this.state.feedbackStatus}
        message={this.state.feedbackMessage}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={this.state.showDeleteDialog}
        onClose={() => this.setState({ showDeleteDialog: false })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this category?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => this.setState({ showDeleteDialog: false })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              this.removeCategoryRow(this.state.rowToDelete);
              this.setState({
                showDeleteDialog: false,
                rowToDelete: null,
              });
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Form Section */}
      <div className="transaction-container">
        <div className="transaction-form-section">
          {categories.map((category, index) => {
            // ✅ Determine editability rules
            const isReadOnly =
              // Global categories are editable only by owners
              category.isGlobal && !hasCompanyIds
                ? true
                : false;

            const canEdit =
              hasCompanyIds || category.isNew || !category.isGlobal;

            return (
              <div className="category-row" key={index}>
                <Tooltip
                  title={
                    isReadOnly
                      ? 'Cannot delete this category'
                      : 'Remove category'
                  }
                >
                  <IconButton
                    onClick={() =>
                      !isReadOnly &&
                      this.setState({
                        showDeleteDialog: true,
                        rowToDelete: index,
                      })
                    }
                    disabled={isReadOnly}
                  >
                    <RemoveCircleIcon
                      fontSize="medium"
                      style={{ color: isReadOnly ? 'gray' : 'red' }}
                    />
                  </IconButton>
                </Tooltip>

                {/* ✅ Category Name */}
                <TextField
                  fullWidth
                  variant="outlined"
                  name="name"
                  label="Category"
                  InputLabelProps={{ shrink: true }}
                  value={category.name || ''}
                  onChange={(e) =>
                    canEdit && this.handleCategoryChange(e, index)
                  }
                  required
                  error={
                    !category.name ||
                    categories.some(
                      (m, i) => m.name === category.name && i !== index
                    )
                  }
                  helperText={
                    !category.name
                      ? ''
                      : categories.some(
                          (m, i) => m.name === category.name && i !== index
                        )
                      ? 'Duplicate category'
                      : ''
                  }
                  InputProps={{
                    readOnly: !canEdit,
                  }}
                />

                {/* ✅ Expense Type */}
                <Autocomplete
                  className="expense-type"
                  options={expenses}
                  getOptionLabel={(option) => option || ''}
                  value={category.expenseType || ''}
                  onChange={(e, value) => {
                    if (canEdit) this.handleExpenseTypeChange(value, index);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="expenseType"
                      label="Expense Type"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        ...params.InputProps,
                        readOnly: !canEdit,
                      }}
                    />
                  )}
                  disabled={!canEdit}
                />
              </div>
            );
          })}

          <div className="add-category">
            <Tooltip title="Add category">
              <IconButton onClick={this.addCategoryRow}>
                <AddCircleIcon fontSize="medium" color="secondary" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
    </>
  );
}

}

const mapStateToProps = (state) => ({
    userInfo: userInfoSelector(state),
    categories: categorySelector(state),
});

const actionCreators = {
    clearStatus,
    editCategory,
};

export default withShipment(
    {
        mapStateToProps,
        actionCreators,
    },
    TransactionCategory
);
